import { policyType } from '@ourbranch/lookups';

import { currencyFormatter } from 'core/helpers/formatters';

// Agency reps are stored as `<affinity>_<agent code>_<username>` (e.g. MATIC1_293_lcunningham).
// Only the username distinguishes reps within an agency, and the full value pushed the search
// results table past the viewport once a Price column was added (#35673). The caller shows the
// full rep as a tooltip.
export const abbreviateOfferRep = (rep) => {
  if (!rep) {
    return '';
  }
  const username = rep.slice(rep.lastIndexOf('_') + 1);
  if (username === rep || username === '') {
    return rep;
  }
  return `…${username}`;
};

// The products each option type covers, in the order the row names them, with the RateOption
// field holding that product's term total. The Eligible Offers label and the Price column both
// read from this one table: the stacked amounts carry no product names of their own, so their
// order is the only thing tying "$100 / $500" to "Renters & Auto". Note AR reads renters first
// here even though the domain `policyTypeLines.AR` is ['A', 'R'] — this is display order only.
const home = { name: 'Home', total: 'homeTotal' };
const auto = { name: 'Auto', total: 'autoTotal' };
const renters = { name: 'Renters', total: 'rentersTotal' };
const condo = { name: 'Condo', total: 'condoTotal' };
const farm = { name: 'Farm', total: 'farmTotal' };
const OPTION_PRODUCTS = {
  [policyType.Home]: [home],
  [policyType.HomeBundle]: [home],
  [policyType.Auto]: [auto],
  [policyType.AutoBundle]: [auto],
  [policyType.Renters]: [renters],
  [policyType.Condo]: [condo],
  [policyType.Farm]: [farm],
  // FarmHome / FarmRental are variants of the one farm offer: named differently, priced as farm.
  [policyType.FarmHome]: [{ name: 'Home', total: 'farmTotal' }],
  [policyType.FarmRental]: [{ name: 'Rental', total: 'farmTotal' }],
  [policyType.HABundle]: [home, auto],
  [policyType.CABundle]: [condo, auto],
  [policyType.ARBundle]: [renters, auto],
  [policyType.FABundle]: [farm, auto]
};
const productsOf = (option) => OPTION_PRODUCTS[option?.type] ?? [];

// Which bundle the row is labelled as when an offer's options cover more than one, and which
// monoline when they cover no bundle at all.
const BUNDLE_PRECEDENCE = [policyType.HABundle, policyType.CABundle, policyType.ARBundle, policyType.FABundle];
const MONOLINE_PRECEDENCE = ['Home', 'Auto', 'Renters', 'Rental', 'Condo', 'Farm'];

// The products the row is labelled with, in label order: the union of products across every
// option, so Home and Auto monolines alone still read "Home & Auto". `bundleType` is set when
// the label is a bundle, whether or not an option of that type actually exists.
const resolveLabelledProducts = (options) => {
  const named = new Set(options.flatMap((option) => productsOf(option).map((product) => product.name)));
  const bundleType = BUNDLE_PRECEDENCE.find((type) =>
    OPTION_PRODUCTS[type].every((product) => named.has(product.name))
  );
  if (bundleType) {
    return { bundleType, products: OPTION_PRODUCTS[bundleType] };
  }
  const name = MONOLINE_PRECEDENCE.find((candidate) => named.has(candidate));
  return { bundleType: undefined, products: name ? [{ name }] : [] };
};

export const deriveEligibleOffers = (options) => {
  const { products } = resolveLabelledProducts(options ?? []);
  return products.length > 0 ? products.map((product) => product.name).join(' & ') : 'N/A';
};

// The term total of the first single-product option named `name`. Resolved through that option's
// own product so a FarmHome ("Home") reads farmTotal, not homeTotal.
const monolineTotal = (options, name) => {
  const option = options.find((candidate) => {
    const products = productsOf(candidate);
    return products.length === 1 && products[0].name === name;
  });
  return option?.[productsOf(option)[0].total];
};

// One formatted term price per product the label names, in label order ("Renters & Auto" →
// [renters, auto]). A bundle option prices every product from its own (discounted) totals; when
// the label is the union of separate monolines, each product is priced from its monoline, so a
// "Home & Auto" row never shows just one of them. Whole dollars: the column is for comparing
// offers at a glance, not for billing. Empty when the options carry no totals — documents
// indexed before #35673 (and anything older than its backfill window) — so the cell is blank
// rather than $0.
export const deriveOfferTermPrices = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }
  const { bundleType, products } = resolveLabelledProducts(options);
  const bundle = bundleType ? options.find((option) => option?.type === bundleType) : undefined;
  return products
    .map((product) => (bundle ? bundle[product.total] : monolineTotal(options, product.name)))
    .filter((total) => typeof total === 'number' && total > 0)
    .map((total) => currencyFormatter(total, { removeDecimal: true }));
};
